import { Fragment, useMemo, useState } from 'react';
import {
  CW_DATASETS,
  CW_DATASET_LABELS,
  CW_SYNC_ACTIVITY_KINDS,
  CW_SYNC_ACTIVITY_KIND_LABELS,
  CW_SYNC_OUTCOMES,
  type CwDataset,
  type CwSyncActivityEntry,
  type CwSyncActivityFilters,
  type CwSyncActivityKind,
  type CwSyncError,
  type CwSyncOutcome,
} from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { DataState } from '../../components/ui/DataState.js';
import { formatDateTime } from '../../utils/date.js';
import { useCwSyncActivity, useCwSyncErrors } from './api.js';

interface CwSyncActivityLogProps {
  isAdmin: boolean;
}

const OUTCOME_TONES: Record<CwSyncOutcome, 'green' | 'amber' | 'red' | 'blue'> = {
  ok: 'green',
  partial: 'amber',
  failed: 'red',
  running: 'blue',
};

const OUTCOME_LABELS: Record<CwSyncOutcome, string> = {
  ok: 'OK',
  partial: 'Partial',
  failed: 'Failed',
  running: 'Running',
};

const SELECT_CLASS =
  'rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

export function CwSyncActivityLog({ isAdmin }: CwSyncActivityLogProps): JSX.Element | null {
  const [dataset, setDataset] = useState<'' | CwDataset>('');
  const [outcome, setOutcome] = useState<'' | CwSyncOutcome>('');
  const [kind, setKind] = useState<'' | CwSyncActivityKind>('');
  const [since, setSince] = useState('');

  const filters = useMemo<CwSyncActivityFilters>(() => {
    const next: CwSyncActivityFilters = { limit: 200 };
    if (dataset) next.dataset = dataset;
    if (outcome) next.outcome = outcome;
    if (kind) next.kind = kind;
    // A date input yields yyyy-mm-dd; treat it as the start of that day.
    if (since) next.since = `${since}T00:00:00.000Z`;
    return next;
  }, [dataset, outcome, kind, since]);

  const query = useCwSyncActivity(filters);
  const errorsQuery = useCwSyncErrors();

  // The error log retains only the latest run's failures per dataset, so correlate them
  // to a run by request id + dataset (a run-due call can sync several datasets at once).
  const failuresByKey = useMemo(() => {
    const map = new Map<string, CwSyncError[]>();
    for (const err of errorsQuery.data ?? []) {
      if (!err.requestId) continue;
      const key = `${err.requestId}::${err.dataset}`;
      const list = map.get(key) ?? [];
      list.push(err);
      map.set(key, list);
    }
    return map;
  }, [errorsQuery.data]);

  if (!isAdmin) return null;

  const entries = query.data ?? [];
  const hasFilters = Boolean(dataset || outcome || kind || since);

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-700">Sync activity</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              History of ConnectWise sync runs — when each dataset ran, who triggered it, and how
              many records were created, updated, or unchanged.
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            aria-label="Dataset"
            className={SELECT_CLASS}
            value={dataset}
            onChange={(e) => setDataset(e.target.value as '' | CwDataset)}
          >
            <option value="">All datasets</option>
            {CW_DATASETS.map((d) => (
              <option key={d} value={d}>
                {CW_DATASET_LABELS[d]}
              </option>
            ))}
          </select>

          <select
            aria-label="Trigger"
            className={SELECT_CLASS}
            value={kind}
            onChange={(e) => setKind(e.target.value as '' | CwSyncActivityKind)}
          >
            <option value="">All triggers</option>
            {CW_SYNC_ACTIVITY_KINDS.map((k) => (
              <option key={k} value={k}>
                {CW_SYNC_ACTIVITY_KIND_LABELS[k]}
              </option>
            ))}
          </select>

          <select
            aria-label="Outcome"
            className={SELECT_CLASS}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value as '' | CwSyncOutcome)}
          >
            <option value="">All outcomes</option>
            {CW_SYNC_OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {OUTCOME_LABELS[o]}
              </option>
            ))}
          </select>

          <input
            aria-label="Since"
            type="date"
            className={SELECT_CLASS}
            value={since}
            onChange={(e) => setSince(e.target.value)}
          />

          {hasFilters ? (
            <button
              type="button"
              onClick={() => {
                setDataset('');
                setOutcome('');
                setKind('');
                setSince('');
              }}
              className="text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              Clear filters
            </button>
          ) : null}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">When</th>
              <th className="px-4 py-3 font-medium">Dataset</th>
              <th className="px-4 py-3 font-medium">Trigger</th>
              <th className="px-4 py-3 font-medium">Outcome</th>
              <th className="px-4 py-3 text-right font-medium">Created</th>
              <th className="px-4 py-3 text-right font-medium">Updated</th>
              <th className="px-4 py-3 text-right font-medium">Unchanged</th>
              <th className="px-4 py-3 text-right font-medium">Fetched</th>
              <th className="px-4 py-3 font-medium">Triggered by</th>
              <th className="px-4 py-3 text-right font-medium">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={10}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={entries.length === 0}
              emptyTitle="No sync activity"
              emptyDescription={
                hasFilters
                  ? 'No runs match the selected filters.'
                  : 'ConnectWise sync runs will appear here once a dataset syncs.'
              }
            >
              {entries.map((entry) => (
                <ActivityRow
                  key={entry.id}
                  entry={entry}
                  failures={failuresByKey.get(`${entry.requestId}::${entry.dataset}`) ?? []}
                />
              ))}
            </DataState>
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface ActivityRowProps {
  entry: CwSyncActivityEntry;
  failures: CwSyncError[];
}

function ActivityRow({ entry, failures }: ActivityRowProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const hadFailures = entry.outcome === 'partial' || entry.outcome === 'failed';

  return (
    <Fragment>
      <tr className="align-top hover:bg-slate-50">
        <td className="whitespace-nowrap px-4 py-3 text-slate-600">
          {formatDateTime(entry.occurredAt)}
        </td>
        <td className="px-4 py-3">
          <Badge tone="blue">{entry.datasetLabel}</Badge>
        </td>
        <td className="px-4 py-3 text-slate-600">{CW_SYNC_ACTIVITY_KIND_LABELS[entry.kind]}</td>
        <td className="px-4 py-3">
          <Badge tone={OUTCOME_TONES[entry.outcome]}>{OUTCOME_LABELS[entry.outcome]}</Badge>
          {entry.message ? (
            <p className="mt-1 max-w-xs text-xs text-slate-500" title={entry.message}>
              {entry.message}
            </p>
          ) : null}
        </td>
        <td className="px-4 py-3 text-right tabular-nums text-slate-800">{entry.created}</td>
        <td className="px-4 py-3 text-right tabular-nums text-slate-800">{entry.updated}</td>
        <td className="px-4 py-3 text-right tabular-nums text-slate-500">{entry.unchanged}</td>
        <td className="px-4 py-3 text-right tabular-nums text-slate-500">{entry.fetched}</td>
        <td className="px-4 py-3 text-slate-600">{entry.actorName}</td>
        <td className="px-4 py-3 text-right">
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            {expanded ? 'Hide' : 'Details'}
          </button>
        </td>
      </tr>

      {expanded ? (
        <tr className="bg-slate-50">
          <td colSpan={10} className="px-4 py-4">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
              <DetailItem label="Dataset" value={entry.datasetLabel} />
              <DetailItem label="Trigger" value={CW_SYNC_ACTIVITY_KIND_LABELS[entry.kind]} />
              <DetailItem label="Outcome" value={OUTCOME_LABELS[entry.outcome]} />
              <DetailItem label="Triggered by" value={entry.actorName} />
              <DetailItem label="Run completed" value={formatDateTime(entry.occurredAt)} />
              <DetailItem label="Change watermark" value={entry.since ?? '—'} />
              <DetailItem label="Fetched" value={String(entry.fetched)} />
              <DetailItem
                label="Created / Updated / Unchanged"
                value={`${entry.created} / ${entry.updated} / ${entry.unchanged}`}
              />
              <div className="sm:col-span-4">
                <dt className="text-xs uppercase tracking-wide text-slate-500">Request ID</dt>
                <dd className="mt-1 break-all font-mono text-xs text-slate-700">
                  {entry.requestId ?? '—'}
                </dd>
              </div>
              {entry.message ? (
                <div className="sm:col-span-4">
                  <dt className="text-xs uppercase tracking-wide text-slate-500">Message</dt>
                  <dd className="mt-1 text-sm text-slate-700">{entry.message}</dd>
                </div>
              ) : null}
            </dl>

            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Failures and warnings ({failures.length})
              </p>
              {failures.length > 0 ? (
                <ul className="mt-2 space-y-2">
                  {failures.map((failure) => (
                    <li
                      key={failure.id}
                      className="rounded-md bg-white p-3 text-sm ring-1 ring-slate-200"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          tone={
                            failure.scope === 'sync'
                              ? 'red'
                              : failure.scope === 'warning'
                                ? 'gray'
                                : 'amber'
                          }
                        >
                          {failure.scope === 'sync'
                            ? 'Whole sync'
                            : failure.scope === 'warning'
                              ? 'Warning'
                              : 'Record'}
                        </Badge>
                        {failure.recordLabel ? (
                          <span className="font-medium text-slate-700">{failure.recordLabel}</span>
                        ) : failure.cwRecordId ? (
                          <span className="font-medium text-slate-700">#{failure.cwRecordId}</span>
                        ) : null}
                      </div>
                      <p
                        className={`mt-1 ${
                          failure.scope === 'warning' ? 'text-amber-600' : 'text-rose-600'
                        }`}
                      >
                        {failure.message}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-sm text-slate-500">
                  {hadFailures
                    ? 'Detailed failures from this run are no longer retained — only the most recent run’s failures per dataset are kept.'
                    : 'No records failed in this run.'}
                </p>
              )}
            </div>
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
}

function DetailItem({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-800">{value}</dd>
    </div>
  );
}
